import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject, OnChanges, SimpleChanges, DoCheck } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { MaterialModule } from '../../../shared/material.module';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { Country } from '../../../models/country';
import { LargeShareholderCategory } from '../../../models/largeShareholderCategory';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { Helpers } from '../../../utilities/helpers';
import { Subscription } from 'rxjs';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';

@Component({
  selector: 'app-company-vote-proposal-name-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    TextNormalizeDirective
  ],
  templateUrl: './company-vote-proposal-name-form.component.html',
  styleUrls: ['./company-vote-proposal-name-form.component.scss']
})
export class CompanyVoteProposalNameFormComponent implements OnInit, OnDestroy, OnChanges, DoCheck {
  @Input() form!: FormGroup;
  @Input() submitButtonText = 'Submit';
  @Input() isEditMode = false;
  @Input() showYearWarning = false;
  @Input() initialTabIndex = 0;
  @Input() hideActions = false;
  @Output() submit = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() yearChange = new EventEmitter<string>();

  selectedTabIndex = 0;

  private lookupService = inject(LookupDataService);
  private lutService = inject(LutService);

  countries: Country[] = [];
  categories: LargeShareholderCategory[] = [];
  subcategories: LargeShareholderCategory[] = [];
  private allCategories: LargeShareholderCategory[] = [];
  private categoriesLoaded = false;
  private lastCategoryId: string | null = null;
  years: string[] = [];
  private subscriptions: Subscription[] = [];

  constructor(private confirmationDialogService: ConfirmationDialogService) {
    this.years = Helpers.measurements();

    this.lookupService.getTableStream<Country>('countries').subscribe(data => {
      if (data) this.countries = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    const categoriesSub = this.lutService.getLargeShareholderCategories().subscribe(data => {
      if (data) {
        this.allCategories = data;
        this.categories = data.filter(c => c.parentId === null).sort((a, b) => a.name.localeCompare(b.name));
        this.categoriesLoaded = true;

        this.initializeSubcategories();
      }
    });
    this.subscriptions.push(categoriesSub);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['form'] && !changes['form'].firstChange) {
      if (this.categoriesLoaded) {
        this.initializeSubcategories();
      }
    }
  }

  ngDoCheck() {
    if (this.categoriesLoaded && this.form) {
      const currentCategory = this.form.get('category')?.value;
      const currentCategoryId = currentCategory?.id ?? null;

      if (currentCategoryId !== this.lastCategoryId) {
        this.lastCategoryId = currentCategoryId;
        this.initializeSubcategories();
      }
    }
  }

  ngOnInit() {
    this.selectedTabIndex = this.initialTabIndex;

    if (this.isEditMode) {
      const yearSub = this.form.get('year')?.valueChanges.subscribe((year: string) => {
        if (year) {
          this.yearChange.emit(year);
        }
      });
      if (yearSub) this.subscriptions.push(yearSub);
    }

    const categorySub = this.form.get('category')?.valueChanges.subscribe((category: LargeShareholderCategory) => {
      if (category) {
        this.subcategories = this.allCategories
          .filter(c => c.parentId === category.id)
          .sort((a, b) => a.name.localeCompare(b.name));

        const currentSubcategory = this.form.get('subcategory')?.value;
        if (currentSubcategory && currentSubcategory.parentId !== category.id) {
          this.form.patchValue({ subcategory: null });
        }
      } else {
        this.subcategories = [];
        this.form.patchValue({ subcategory: null });
      }
    });
    if (categorySub) this.subscriptions.push(categorySub);

    if (this.categoriesLoaded) {
      this.initializeSubcategories();
    }
  }

  private initializeSubcategories() {
    const currentCategory = this.form.get('category')?.value;
    if (currentCategory) {
      this.subcategories = this.allCategories
        .filter(c => c.parentId === currentCategory.id)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  compareById(option1: any, option2: any): boolean {
    return option1 && option2 ? option1.id === option2.id : option1 === option2;
  }

  onSubmit() {
    if (this.form.valid) {
      const dialogObservable = this.isEditMode
        ? this.confirmationDialogService.openUpdateConfirmationDialog()
        : this.confirmationDialogService.openCreateConfirmationDialog();

      dialogObservable.subscribe((result: boolean) => {
        if (result) {
          this.submit.emit();
        }
      });
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  onTabChange(index: number) {
    this.selectedTabIndex = index;
  }
}
